import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getOfferById } from '@services/offers';
import { mapBackendOfferToFrontend } from '@pages/seeker/OffersForMyPlan';
import type { Offer } from '@data/offers';
import type { PublicPlan } from '@data/publicPlans';
import { getPublicPlanById, getPublicPlanByIdFromAPI } from '@data/publicPlans';
import { useAuth } from '@services/auth';
import { useAdminViewAs, getMessagingActingUserId, getAdminEffectiveCompanyOrGardenerIdNum } from '../../lib/adminViewAs';
import { useI18n } from '../../i18n';
import BackButton from '../../shared/BackButton';
import OfferDetailPreview from '@components/OfferDetailPreview';
import ConversationThread from '@components/ConversationThread';

const STATUS_LABELS: Record<Offer['status'], string> = {
	pending: 'Pending',
	accepted: 'Accepted',
	rejected: 'Rejected'
};

export default function SentOfferView() {
	const { offerId } = useParams<{ offerId: string }>();
	const { user } = useAuth();
	const { viewAsTarget } = useAdminViewAs();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const locale = language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB';
	const [offer, setOffer] = useState<Offer | null>(null);
	const [plan, setPlan] = useState<PublicPlan | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [messageOpen, setMessageOpen] = useState(false);

	useEffect(() => {
		const id = offerId ? parseInt(offerId, 10) : NaN;
		const gardenerActorId = getAdminEffectiveCompanyOrGardenerIdNum(user, viewAsTarget);
		if (!offerId || isNaN(id)) {
			setError('Invalid offer');
			setIsLoading(false);
			return;
		}
		if (!user) {
			setError('You must be logged in to view this offer.');
			setIsLoading(false);
			return;
		}
		if (gardenerActorId == null) {
			setError(
				user.role === 'admin'
					? 'Use “View as user…” and pick the company or gardener who sent this offer.'
					: 'You do not have access to this offer.'
			);
			setIsLoading(false);
			return;
		}

		let isMounted = true;
		getOfferById(id, { gardenerId: gardenerActorId })
			.then((res) => {
				if (!isMounted) return;
				const mapped = mapBackendOfferToFrontend(res);
				setOffer(mapped);
				setError(null);
				// Load plan for fallback when offer has no snapshot
				const planId = mapped.planId;
				const cached = getPublicPlanById(planId);
				if (cached) {
					setPlan(cached);
					return;
				}
				getPublicPlanByIdFromAPI(planId).then((p) => {
					if (isMounted && p) setPlan(p);
				}).catch(() => {});
			})
			.catch((err) => {
				if (!isMounted) return;
				console.error('Failed to load offer:', err);
				setError('Offer not found or you don’t have access.');
				setOffer(null);
			})
			.finally(() => {
				if (isMounted) setIsLoading(false);
			});

		return () => { isMounted = false; };
	}, [offerId, user, viewAsTarget]);

	if (user && user.role !== 'gardener' && user.role !== 'company' && user.role !== 'admin') {
		return (
			<div className="alert alert-info">
				Sent offer view is only available for gardeners and companies.
			</div>
		);
	}

	if (isLoading) {
		return (
			<>
				<BackButton fallbackPath="/app/projects?tab=offers" label="Back to Sent offers" size="sm" forceFallback />
				<div className="text-center py-5">
					<div className="spinner-border text-success" role="status">
						<span className="visually-hidden">Loading...</span>
					</div>
					<p className="text-muted mt-2 mb-0">Loading offer...</p>
				</div>
			</>
		);
	}

	if (error || !offer) {
		return (
			<>
				<BackButton fallbackPath="/app/projects?tab=offers" label="Back to Sent offers" size="sm" forceFallback />
				<div className="alert alert-warning">
					{error ?? 'Offer not found.'}
				</div>
			</>
		);
	}

	const planDisplayName = offer.planTitle?.trim() || `Plan #${offer.planId.replace(/^db-/, '')}`;

	const openMessageModal = () => {
		window.dispatchEvent(new Event('app:clear-search-focus'));
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
		document.querySelectorAll<HTMLInputElement>('input[type="search"]').forEach((input) => input.blur());
		setTimeout(() => {
			if (document.activeElement instanceof HTMLElement) {
				document.activeElement.blur();
			}
			document.querySelectorAll<HTMLInputElement>('input[type="search"]').forEach((input) => input.blur());
		}, 0);
		setMessageOpen(true);
	};

	return (
		<>
			<div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
				<BackButton fallbackPath="/app/projects?tab=offers" label="Back to Sent offers" size="sm" forceFallback />
				<div className="d-flex align-items-center gap-2">
					<span
						className={`badge ${
							offer.status === 'accepted'
								? 'bg-success'
								: offer.status === 'rejected'
									? 'bg-secondary'
									: 'bg-warning text-dark'
						}`}
					>
						{STATUS_LABELS[offer.status]}
					</span>
					<button
						type="button"
						className="btn btn-outline-primary btn-sm"
						onClick={openMessageModal}
						title={copy('Message seeker', 'Nachricht an Suchenden', 'Poruka vlasniku')}
					>
						<i className="bi bi-chat-dots me-1" aria-hidden></i>
						{copy('Message seeker', 'Nachricht an Suchenden', 'Poruka vlasniku')}
					</button>
				</div>
			</div>
			<div className="card border-0 shadow-sm mb-4">
				<div className="card-header bg-white py-2">
					<h5 className="mb-0">{planDisplayName}</h5>
					<p className="text-muted small mb-0 mt-1">
						{copy('Offer sent', 'Angebot gesendet', 'Ponuda poslana')} {offer.createdAt ? new Date(offer.createdAt).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
					</p>
				</div>
			</div>
			<div className="card border-0 shadow-sm">
				<OfferDetailPreview offer={offer} plan={plan} viewAs="sender" planId={offer.planId} />
			</div>
			{messageOpen && user && (() => {
				const offerIdNum = parseInt(offer.id, 10);
				const userIdNum = getMessagingActingUserId(user, viewAsTarget);
				if (isNaN(offerIdNum) || userIdNum == null || isNaN(userIdNum)) return null;
				return (
					<>
						<div className="modal fade show" style={{ display: 'block', zIndex: 20000 }} role="dialog" aria-modal="true" aria-labelledby="sentOfferViewMessageTitle">
							<div className="modal-dialog modal-dialog-centered">
								<div className="modal-content">
									<div className="modal-header">
										<h5 className="modal-title" id="sentOfferViewMessageTitle">{copy('Message about this offer', 'Nachricht zu diesem Angebot', 'Poruka o ovoj ponudi')}</h5>
										<button type="button" className="btn-close" aria-label="Close" onClick={() => setMessageOpen(false)} />
									</div>
									<div className="modal-body">
										<ConversationThread
											variant="offer"
											offerId={offerIdNum}
											currentUserId={userIdNum}
											title={copy('Conversation with seeker', 'Unterhaltung mit Suchendem', 'Razgovor s vlasnikom')}
											onClose={() => setMessageOpen(false)}
										/>
									</div>
								</div>
							</div>
						</div>
						<div className="modal-backdrop fade show" style={{ zIndex: 19990 }} />
					</>
				);
			})()}
		</>
	);
}
